import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AiFillHeart, AiOutlineClose } from 'react-icons/ai';
import { FaWhatsapp } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { add_to_card, add_to_wishlist } from '../store/reducers/cardReducer';
import { getProxiedUrl } from '../utils/imageUtils';
import { getDisplayPrice } from '../utils/priceCalculator';
import toast from 'react-hot-toast';

const ProductDetailsModal = ({ product, isOpen, onClose, sellerName, sellerId, supportInfo }) => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(product?.images?.[0] || '');

  if (!isOpen || !product) return null;

  const inc = () => {
    setQuantity(quantity + 1);
  };

  const dec = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const addToCart = () => {

    if (userInfo) {
      dispatch(
        add_to_card({
          userId: userInfo.id,
          quantity,
          productId: product._id,
          addons: [],
          price: product.price,
          bulkPricing: product.bulkPricing,
          discountPrice: calculateTotalPrice(),
          sellerId: sellerId,
          packSize: product.packSize,
          selectedImage: selectedImage,
        })
      );
    } else {
      dispatch(
        add_to_card({
          userId: null,
          quantity,
          productId: product._id,
          addons: [],
          price: product.price,
          bulkPricing: product.bulkPricing,
          discountPrice: calculateTotalPrice(),
          sellerId: sellerId,
          packSize: product.packSize,
          selectedImage: selectedImage,
        })
      );
    }
    toast.success("Product added to cart!");
  };

  const addToWishlist = () => {
    if (userInfo) {
      dispatch(
        add_to_wishlist({
          userId: userInfo.id,
          productId: product._id,
          name: product.name,
          price: product.price,
          image: product.images[0],
          discount: product.discount,
          rating: product.rating,
          slug: product.slug,
          currency: product.currency,
        })
      );
    } else {
      const sessionId = Date.now().toString();
      dispatch(
        add_to_wishlist({
          sessionId: sessionId,
          productId: product._id,
          name: product.name,
          price: product.price,
          image: product.images[0],
          discount: product.discount,
          rating: product.rating,
          slug: product.slug,
          currency: product.currency,
        })
      );
    }
    toast.success("Product added to wishlist!");
  };

  const calculateTotalPrice = () => {
    let basePrice = product.price;
    
    if (product.discount > 0) {
      const discountAmount = (basePrice * product.discount) / 100;
      basePrice = basePrice - discountAmount;
    }

    return basePrice * quantity;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    const countryCode = '27'; // Default to South Africa
    if (cleaned.startsWith('0')) {
      return countryCode + cleaned.substring(1);
    }
    if (!cleaned.startsWith(countryCode)) {
      return countryCode + cleaned;
    }
    return cleaned;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex flex-col lg:flex-row">
          {/* Product Images */}
          <div className="lg:w-1/2 p-6">
            <div className="relative">
              <button
                onClick={onClose}
                className="absolute top-2 right-2 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
              >
                <AiOutlineClose className="text-gray-600" />
              </button>
              
              <div className="relative w-full h-80 mb-4">
                <img
                  className="w-full h-full object-contain rounded-lg"
                  src={getProxiedUrl(selectedImage || product.images?.[0])}
                  alt={product.name}
                />
                {product.discount > 0 && (
                  <div className="absolute top-4 left-4 bg-red-500 text-white text-sm px-3 py-1 rounded-full font-medium">
                    -{product.discount}% OFF
                  </div>
                )}
              </div>
              
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {product.images.slice(0, 4).map((img, i) => (
                    <div 
                      key={i}
                      onClick={() => setSelectedImage(img)}
                      className={`cursor-pointer border-2 rounded-lg overflow-hidden ${
                        selectedImage === img ? "border-indigo-500" : "border-gray-100"
                      }`}
                    >
                      <img
                        className="w-full h-20 object-contain"
                        src={getProxiedUrl(img)}
                        alt=""
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="lg:w-1/2 p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              {product.name}
            </h2>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-1">
                <span className="text-yellow-400">★</span>
                <span className="text-gray-600">{product.rating || 0}</span>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div>
                {(() => {
                  const display = getDisplayPrice(product);
                  return (
                    <>
                      <span className="text-2xl font-bold text-indigo-600">
                        {display.currency} {Number(display.price).toFixed(2)}
                        {display.isBulk && (
                          <span className="text-lg font-semibold text-gray-700 ml-1">for {display.quantity}</span>
                        )}
                      </span>
                      {display.isBulk && (
                        <div className="text-sm text-gray-500 mt-1">
                          {display.currency} {Number(display.perItemPrice).toFixed(2)} per item
                        </div>
                      )}
                      {!display.isBulk && quantity > 1 && (
                        <div className="text-sm text-gray-500 mt-1">
                          {product.currency} {(calculateTotalPrice() / quantity).toFixed(2)} per item
                        </div>
                      )}
                      {quantity > 1 && (
                        <div className="text-sm font-medium text-gray-700 mt-1">
                          Total: {product.currency} {calculateTotalPrice().toFixed(2)}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              {!getDisplayPrice(product).isBulk && product.discount > 0 && (
                <span className="text-lg text-gray-500 line-through">
                  {product.currency} {(product.price * quantity).toFixed(2)}
                </span>
              )}
            </div>

            {(product.showStockQuantity !== false || product.stock <= 0) && (
              <div className="flex items-center gap-2 mb-6">
                <span className={`w-3 h-3 rounded-full ${product.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span className="text-base text-gray-700 font-medium">
                  {product.stock > 0
                    ? (product.showStockQuantity ? `In stock: ${product.stock}` : 'In stock')
                    : 'Out of stock'}
                </span>
              </div>
            )}

            {product.description && (
              <div className="text-gray-600 mb-6 leading-relaxed">
                {product.description.length > 200 
                  ? `${product.description.substring(0, 200)}...` 
                  : product.description
                }
              </div>
            )}

            {/* Quantity and Add to Cart */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <button
                  onClick={dec}
                  className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  -
                </button>
                <span className="w-12 text-center text-gray-800">{quantity}</span>
                <button
                  onClick={inc}
                  className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  +
                </button>
              </div>
              <button
                onClick={addToCart}
                className="flex-1 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-colors duration-200 font-medium"
              >
                Add to Cart
              </button>
              <button
                onClick={addToWishlist}
                className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                <AiFillHeart className="text-red-500 text-xl" />
              </button>
            </div>

            {/* Contact Seller Button */}
            <div className="flex gap-4 mb-6">
              <a
                href={`https://wa.me/${formatPhoneNumber(supportInfo?.mobileNo)}?text=Hi, I'm interested in your product: ${product.name}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 rounded-lg hover:bg-[#128C7E] transition-colors duration-200 font-medium"
              >
                <FaWhatsapp className="text-xl" />
                WhatsApp
              </a>
            </div>

            {/* View Full Details Button */}
            <div className="pt-4 border-t border-gray-100">
              <Link
                to={`/${sellerName}/product/details/${product.slug}`}
                onClick={onClose}
                className="w-full bg-gray-100 text-gray-800 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium text-center block"
              >
                View Full Details
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;
